import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../services/api';
import { getContactInitials } from '../utils/contactInitials';
import { getMainEmail, getMainPhone } from '../utils/contactProfile';
import { formatPhoneFR } from '../utils/formatFR';
import { exportContactsCSV, exportContactsVCard } from '../utils/contactExport';
import ContactBulkEmailModal from '../components/contacts/ContactBulkEmailModal';
import ContactBulkArchiveModal from '../components/contacts/ContactBulkArchiveModal';
import BulkTagModal from '../components/contacts/BulkTagModal';

/**
 * GroupesPage - Vue 2 colonnes : groupes (tags) à gauche, contacts du groupe à droite.
 * Lot Productivité : sélection multiple + actions groupées (email, impression, export
 * CSV/vCard, tag en masse, archiver, supprimer), alignées sur la liste Contacts.
 */
const GroupesPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [search, setSearch] = useState('');

  // Sélection + actions groupées
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exportMenu, setExportMenu] = useState(false);
  const [archiveModal, setArchiveModal] = useState({ open: false, items: [] });
  const [archiveBusy, setArchiveBusy] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contacts?status=active');
      setContacts(data || []);
    } catch (e) {
      console.error('Erreur chargement contacts:', e);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const groupsMap = useMemo(() => {
    const map = new Map();
    contacts.forEach((c) => {
      const groupes = Array.isArray(c.groupes) ? c.groupes : [];
      groupes.forEach((g) => {
        if (!g) return;
        const key = g.trim();
        if (!key) return;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(c);
      });
    });
    return map;
  }, [contacts]);

  const sortedGroups = useMemo(
    () => Array.from(groupsMap.keys()).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })),
    [groupsMap]
  );

  useEffect(() => {
    if (!selectedGroup && sortedGroups.length > 0) setSelectedGroup(sortedGroups[0]);
  }, [sortedGroups, selectedGroup]);

  // Réinitialiser la sélection au changement de groupe
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedGroup]);

  // Fermer le menu déroulant Export au clic extérieur
  useEffect(() => {
    if (!exportMenu) return undefined;
    const close = () => setExportMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [exportMenu]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return sortedGroups;
    const q = search.trim().toLowerCase();
    return sortedGroups.filter((g) => g.toLowerCase().includes(q));
  }, [sortedGroups, search]);

  const contactsForSelected = selectedGroup ? groupsMap.get(selectedGroup) || [] : [];
  const selectedContacts = useMemo(
    () => contactsForSelected.filter((c) => selectedIds.includes(c.id)),
    [contactsForSelected, selectedIds]
  );
  const allSelected = contactsForSelected.length > 0 && selectedIds.length === contactsForSelected.length;

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? [] : contactsForSelected.map((c) => c.id));

  // ─── Actions groupées ───────────────────────────────────
  const handleExport = (kind) => {
    setExportMenu(false);
    if (selectedContacts.length === 0) return;
    const base = (selectedGroup || 'contacts').replace(/\s+/g, '_');
    if (kind === 'csv') exportContactsCSV(selectedContacts, `${base}_${selectedContacts.length}.csv`);
    else exportContactsVCard(selectedContacts, `${base}_${selectedContacts.length}.vcf`);
    toast.success(`${selectedContacts.length} contact(s) exporté(s) (${kind.toUpperCase()})`);
  };

  const handlePrint = async () => {
    if (selectedIds.length === 0) return;
    setPrinting(true);
    try {
      const res = await api.post(
        `/contacts/bulk-fiche-contact?version=agent`,
        { contact_ids: selectedIds },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiches_contacts_${selectedIds.length}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedIds.length} fiche(s) contact générée(s)`);
    } catch (e) {
      console.error('Erreur impression groupée:', e);
      toast.error('Erreur lors de la génération du PDF groupé');
    } finally {
      setPrinting(false);
    }
  };

  // Archivage groupé : vérifie les biens liés (cascade) avant d'archiver
  const handleBulkArchiveClick = async () => {
    if (selectedIds.length === 0) return;
    try {
      const results = await Promise.all(
        selectedIds.map(async (id) => {
          const { data } = await api.get(`/contacts/${id}/linked-properties`);
          const c = contacts.find((x) => x.id === id) || { id };
          return { contact: { id, prenom: c.prenom, nom: c.nom }, properties: data.linked_properties || [] };
        })
      );
      const withBiens = results.filter((r) => r.properties.length > 0);
      if (withBiens.length === 0) {
        if (window.confirm(`Archiver ${selectedIds.length} contact(s) ?`)) {
          await api.post('/contacts/bulk-archive', selectedIds);
          toast.success(`${selectedIds.length} contact(s) archivé(s)`);
          setSelectedIds([]);
          loadContacts();
        }
      } else {
        setArchiveModal({ open: true, items: withBiens });
      }
    } catch (e) {
      console.error('Erreur archivage groupé:', e);
      toast.error('Erreur lors de la vérification des biens liés');
    }
  };

  const runBulkArchive = async (cascade) => {
    setArchiveBusy(true);
    try {
      const itemsById = {};
      archiveModal.items.forEach((it) => {
        itemsById[it.contact.id] = it.properties.map((p) => p.id);
      });
      await Promise.all(
        selectedIds.map((id) =>
          api.post(`/contacts/${id}/archive`, { cascade_property_ids: cascade ? itemsById[id] || [] : [] })
        )
      );
      setArchiveModal({ open: false, items: [] });
      setSelectedIds([]);
      loadContacts();
      toast.success('Contacts archivés');
    } catch (e) {
      console.error('Erreur archivage cascade:', e);
      toast.error("Erreur lors de l'archivage groupé");
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selectedIds.length} contact(s) ?`)) return;
    try {
      const { data } = await api.post('/contacts/bulk-delete', selectedIds);
      if (data.blocked?.length > 0) {
        const names = data.blocked.map((b) => b.nom).join(', ');
        toast.warning(`${data.deleted_count} supprimé(s). ${data.blocked.length} bloqué(s) (biens actifs liés) : ${names}`);
      } else {
        toast.success(`${data.deleted_count} contact(s) supprimé(s)`);
      }
      setSelectedIds([]);
      loadContacts();
    } catch (e) {
      console.error('Erreur suppression groupée:', e);
      toast.error('Erreur lors de la suppression groupée');
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-4 bg-slate-50 min-h-screen" data-testid="groupes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <i className="bi bi-collection text-blue-600 text-xl sm:text-2xl"></i>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Groupes de contacts</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Vos contacts segmentés par tag (groupe)</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/contacts')}
          className="px-4 py-2 bg-white border border-slate-300 hover:border-blue-400 hover:text-blue-600 text-slate-700 rounded-lg text-sm font-medium transition flex items-center gap-2"
          data-testid="groupes-back-contacts"
        >
          <i className="bi bi-arrow-left"></i>
          Tous les contacts
        </button>
      </div>

      {/* Tableau 2 colonnes */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[60vh]">
          {/* Col 1: Groupes */}
          <div className="border-b md:border-b-0 md:border-r border-slate-200 flex flex-col">
            <div className="p-3 border-b border-slate-200 bg-slate-50">
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un groupe..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  data-testid="groupes-search-input"
                />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {sortedGroups.length} groupe{sortedGroups.length > 1 ? 's' : ''}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Chargement...
                </div>
              ) : filteredGroups.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  <i className="bi bi-inbox text-3xl text-slate-300 block mb-2"></i>Aucun groupe trouvé
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredGroups.map((g) => {
                    const count = groupsMap.get(g)?.length || 0;
                    const active = selectedGroup === g;
                    return (
                      <li key={g}>
                        <button
                          onClick={() => setSelectedGroup(g)}
                          className={`w-full text-left px-4 py-3 flex items-center justify-between transition ${
                            active ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-slate-50 border-l-4 border-transparent'
                          }`}
                          data-testid={`groupe-row-${g}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <i className={`bi bi-tag-fill ${active ? 'text-blue-600' : 'text-slate-400'}`}></i>
                            <span className={`text-sm font-medium truncate ${active ? 'text-blue-800' : 'text-slate-700'}`}>{g}</span>
                          </div>
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ml-2 ${
                              active ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Col 2: Contacts du groupe sélectionné */}
          <div className="flex flex-col">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-800 truncate flex items-center gap-2">
                  <i className="bi bi-people text-blue-600"></i>
                  {selectedGroup || 'Sélectionnez un groupe'}
                </h2>
                {selectedGroup && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {contactsForSelected.length} contact{contactsForSelected.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              {selectedGroup && contactsForSelected.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  className="px-3 py-1.5 bg-white border border-slate-300 hover:border-blue-400 hover:text-blue-600 text-slate-700 rounded-lg text-xs font-medium transition flex items-center gap-1.5"
                  data-testid="groupes-select-all"
                >
                  <i className={`bi ${allSelected ? 'bi-check-square-fill text-blue-600' : 'bi-square'}`}></i>
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              )}
            </div>

            {/* Barre d'actions groupées */}
            {selectedIds.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-2 p-3 bg-blue-50 border-b border-blue-100"
                data-testid="groupes-bulk-bar"
              >
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <i className="bi bi-check2-circle text-blue-500"></i>
                  {selectedIds.length} sélectionné(s)
                </span>
                <div className="flex flex-wrap gap-2 sm:ml-auto">
                  <button
                    onClick={() => setBulkEmailOpen(true)}
                    aria-label="Email"
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                    data-testid="groupes-bulk-email"
                  >
                    <i className="bi bi-envelope"></i><span className="hidden sm:inline">Email</span>
                  </button>

                  {/* Imprimer — fiche contact complète (agents immo) */}
                  <button
                    onClick={() => handlePrint()}
                    disabled={printing}
                    aria-label="Imprimer"
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                    data-testid="groupes-bulk-print"
                  >
                    {printing ? <i className="bi bi-hourglass-split animate-spin"></i> : <i className="bi bi-printer"></i>}
                    <span className="hidden sm:inline">Imprimer</span>
                  </button>

                  {/* Exporter */}
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setExportMenu((v) => !v); }}
                      aria-label="Exporter"
                      className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                      data-testid="groupes-bulk-export"
                    >
                      <i className="bi bi-download"></i><span className="hidden sm:inline">Exporter</span>
                      <i className="bi bi-chevron-down text-xs text-slate-400"></i>
                    </button>
                    {exportMenu && (
                      <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-xl z-[60] py-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleExport('csv')} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="groupes-export-csv">
                          <i className="bi bi-filetype-csv text-emerald-600"></i>CSV (Excel)
                        </button>
                        <button onClick={() => handleExport('vcard')} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="groupes-export-vcard">
                          <i className="bi bi-person-vcard text-blue-600"></i>vCard (.vcf)
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setTagModalOpen(true)}
                    aria-label="Tag en masse"
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                    data-testid="groupes-bulk-tag"
                  >
                    <i className="bi bi-tags"></i><span className="hidden sm:inline">Tag</span>
                  </button>
                  <button
                    onClick={handleBulkArchiveClick}
                    aria-label="Archiver"
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                    data-testid="groupes-bulk-archive"
                  >
                    <i className="bi bi-archive"></i><span className="hidden sm:inline">Archiver</span>
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    aria-label="Supprimer"
                    className="px-3 py-1.5 bg-white border border-slate-200 text-red-600 hover:bg-red-50 hover:border-red-200 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                    data-testid="groupes-bulk-delete"
                  >
                    <i className="bi bi-trash"></i><span className="hidden sm:inline">Supprimer</span>
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    className="px-3 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg text-sm font-medium transition"
                    data-testid="groupes-bulk-cancel"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {!selectedGroup ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  <i className="bi bi-arrow-left-circle text-3xl text-slate-300 block mb-2"></i>
                  Sélectionnez un groupe à gauche pour voir ses contacts
                </div>
              ) : contactsForSelected.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  <i className="bi bi-person-x text-3xl text-slate-300 block mb-2"></i>Aucun contact dans ce groupe
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          data-testid="groupes-header-checkbox"
                        />
                      </th>
                      <th className="px-4 py-2 text-left font-semibold">Contact</th>
                      <th className="px-4 py-2 text-left font-semibold hidden sm:table-cell">Type</th>
                      <th className="px-4 py-2 text-left font-semibold hidden md:table-cell">Email</th>
                      <th className="px-4 py-2 text-left font-semibold hidden lg:table-cell">Téléphone</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {contactsForSelected.map((c) => {
                      const initials = getContactInitials(c.prenom, c.nom);
                      const fullName = `${c.prenom || ''} ${c.nom || ''}`.trim() || 'Contact';
                      const email = getMainEmail(c);
                      const phone = getMainPhone(c);
                      const checked = selectedIds.includes(c.id);
                      return (
                        <tr
                          key={c.id}
                          className={`hover:bg-slate-50 transition cursor-pointer ${checked ? 'bg-blue-50/50' : ''}`}
                          onClick={() => navigate(`/contacts/${c.id}`)}
                          data-testid={`groupe-contact-row-${c.id}`}
                        >
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelect(c.id)}
                              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                              data-testid={`groupe-contact-check-${c.id}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {c.photo_url ? (
                                <img src={c.photo_url} alt={fullName} className="w-9 h-9 rounded-full object-cover bg-slate-100 shrink-0" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                                  {initials}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="font-medium text-slate-800 truncate">{fullName}</div>
                                {(c.societe || c.nom_societe) && (
                                  <div className="text-xs text-slate-500 truncate">{c.societe || c.nom_societe}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                              {c.type_contact || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                            {email ? (
                              <a
                                href={`mailto:${email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-slate-600 hover:text-blue-600 hover:underline truncate inline-block max-w-[220px] align-middle"
                                data-testid={`groupe-contact-email-${c.id}`}
                              >
                                {email}
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell text-slate-600">
                            {phone ? (
                              <a
                                href={`tel:${phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-slate-600 hover:text-blue-600 hover:underline"
                                data-testid={`groupe-contact-phone-${c.id}`}
                              >
                                {formatPhoneFR(phone)}
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <ContactBulkEmailModal
        open={bulkEmailOpen}
        contactIds={selectedIds}
        onClose={() => setBulkEmailOpen(false)}
        onSent={(res) => {
          if (res?.sent != null) toast.success(`${res.sent} email(s) envoyé(s)${res.failed ? ` · ${res.failed} échec(s)` : ''}`);
          setSelectedIds([]);
        }}
      />
      <BulkTagModal
        open={tagModalOpen}
        contactIds={selectedIds}
        existingTags={sortedGroups}
        onClose={() => setTagModalOpen(false)}
        onDone={() => {
          setTagModalOpen(false);
          setSelectedIds([]);
          loadContacts();
        }}
      />
      <ContactBulkArchiveModal
        open={archiveModal.open}
        items={archiveModal.items}
        totalSelected={selectedIds.length}
        busy={archiveBusy}
        onClose={() => setArchiveModal({ open: false, items: [] })}
        onArchiveAll={() => runBulkArchive(true)}
        onArchiveContactsOnly={() => runBulkArchive(false)}
      />
    </div>
  );
};

export default GroupesPage;
